import { useEffect } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';
import { change, getFormValues, isSubmitting, touch } from 'redux-form';

import i18n, { useTransl } from 'shared/lib/i18n';

import AnnouncementsModalComponent from './AnnouncementsModalComponent';

import { getBuildingsForFilter } from '../Building/actionCreators';
import { onModalClose, showConfirmModal } from '../Modal/actions';
import { getAllPmcReducedData } from '../PMC/actionCreators';
import {
  createAnnouncement,
  deleteAnnouncement,
  sendAnnouncement,
  updateAnnouncement,
} from './actions';

import { getRoleIdByName, getRoleName } from '../Person/utils';

import * as buildingSelectors from '../Building/selectors';
import * as pmcSelectors from '../PMC/selectors';

const validate = (values) => {
  const { roles = {} } = values;
  const errors = {};
  const selectedRolesLength = Object.values(roles).filter(
    (isSelected) => isSelected,
  ).length;
  if (!selectedRolesLength) {
    errors.roles = {
      pmc_owner: i18n.t('errors.oneRoleRequired'),
    };
  }
  return errors;
};

const form = 'announcements';

const AnnouncementsModalContainer = (props) => {
  const { t } = useTransl();
  const {
    onGetBuildings,
    onUpdateAnnouncement,
    onCreateAnnouncement,
    onSendAnnouncement,
    onDeleteAnnouncement,
    onFieldsTouch,
    onFieldChange,
    onModalClose,
    formValues,
    getAnnouncements,
    listParams,
    initialValues,
    buildings,
    onShowConfirmModal,
  } = props;

  const {
    all_properties,
    launch_date_after,
    launch_date_before,
    access_control_system,
    camera_system,
    buildings: selectedBuildings,
    sent,
    pmcs,
  } = formValues;

  const {
    launch_date_after: initial_launch_date_after,
    launch_date_before: initial_launch_date_before,
    access_control_system: initial_access_control_system,
    camera_system: initial_camera_system,
  } = initialValues;

  const shouldClearBuildings =
    launch_date_after !== initial_launch_date_after ||
    launch_date_before !== initial_launch_date_before ||
    access_control_system !== initial_access_control_system ||
    camera_system !== initial_camera_system;

  const hasFormValues =
    launch_date_after !== undefined &&
    launch_date_before !== undefined &&
    access_control_system !== undefined &&
    camera_system !== undefined;

  useEffect(() => {
    if (!hasFormValues) return;

    const getAvailableBuildings = async () => {
      const res = await onGetBuildings({
        launch_date_after,
        launch_date_before,
        access_control_system,
        camera_system,
      });
      return res?.data;
    };
    const availableBuildings = getAvailableBuildings();

    if (selectedBuildings?.length && availableBuildings?.length) {
      const relevantBuildings = selectedBuildings.filter((selectedBuildingId) =>
        availableBuildings?.find(({ id }) => id === selectedBuildingId),
      );
      onFieldChange('buildings', relevantBuildings);
    } else {
      shouldClearBuildings && onFieldChange('buildings', []);
    }
    shouldClearBuildings && onFieldChange('all_properties', false);
  }, [
    launch_date_after,
    launch_date_before,
    access_control_system,
    camera_system,
  ]);

  useEffect(() => {
    if (buildings?.length && selectedBuildings?.length) {
      const relevantBuildings = selectedBuildings.filter((selectedBuildingId) =>
        buildings?.find(({ id }) => id === selectedBuildingId),
      );
      onFieldChange('buildings', relevantBuildings);
    }
  }, [pmcs]);

  useEffect(() => {
    if (all_properties) {
      const buildingsIds = buildings?.map(({ id }) => id) || [];
      onFieldChange('buildings', buildingsIds);
    } else {
      onFieldChange('buildings', []);
    }
  }, [all_properties]);

  const formatValues = async (values) => {
    const {
      id,
      roles,
      allProperties,
      pmcs,
      buildings: selectedBuildingsIds,
      launch_date_after,
      launch_date_before,
      ...rest
    } = values;

    const rolesIds = Object.keys(roles)
      .filter((roleName) => roles[roleName])
      .map((roleName) => getRoleIdByName(roleName));

    let buildingsIds = selectedBuildingsIds;
    if (pmcs?.length) {
      buildingsIds = buildings
        ?.filter(({ id }) => selectedBuildingsIds?.includes(id))
        ?.filter(({ parent }) => pmcs?.includes(parent?.id))
        ?.map(({ id }) => id);
    }

    const announcementData = {
      ...rest,
      properties: buildingsIds,
      roles: rolesIds,
      startLaunchTime: launch_date_after,
      endLaunchTime: launch_date_before,
    };
    return {
      id,
      announcementData,
    };
  };

  const onSaveAnnouncement = async () => {
    onFieldsTouch(['subject', 'message', 'buildings', 'roles.pmc_owner']);

    const data = await formatValues(formValues);
    const { id, announcementData } = data;
    const { roles, properties, subject, message } = announcementData || {};

    if (!roles?.length || !properties?.length || !subject || !message) return;

    const announcementInfo = id
      ? await onUpdateAnnouncement(id, announcementData)
      : await onCreateAnnouncement(announcementData);
    if (announcementInfo?.data?.id) {
      onModalClose();
      getAnnouncements(listParams);
    }
  };

  const onAnnouncementsSubmit = async (values) => {
    const data = await formatValues(values);
    const { id, announcementData } = data;
    const announcementInfo = id
      ? await onUpdateAnnouncement(id, announcementData)
      : await onCreateAnnouncement(announcementData);

    const announcementId = announcementInfo?.data?.id;
    if (!announcementId) return;
    !sent && (await onSendAnnouncement(announcementId));
    onModalClose();
    getAnnouncements(listParams);
  };

  const onAnnouncementDelete = async (id) => {
    await onDeleteAnnouncement(id);
    onModalClose();
    getAnnouncements(listParams);
  };

  const onConfirmUpdate = (values) => {
    const modalProps = {
      bodyText: t('announcements.editWarning'),
      onConfirm: () => onAnnouncementsSubmit(values),
      onCancel: () => {},
      confirmText: t('actions.save'),
      cancelText: t('actions.cancel'),
    };
    onShowConfirmModal(modalProps);
  };

  const onConfirmDelete = (id) => {
    const modalProps = {
      bodyText: t('announcements.deleteWarning'),
      onConfirm: () => onAnnouncementDelete(id),
      onCancel: () => {},
      confirmText: t('actions.delete'),
      cancelText: t('actions.cancel'),
    };
    onShowConfirmModal(modalProps);
  };

  return (
    <AnnouncementsModalComponent
      {...props}
      onSubmit={sent ? onConfirmUpdate : onAnnouncementsSubmit}
      onSave={onSaveAnnouncement}
      onDelete={sent ? onConfirmDelete : onAnnouncementDelete}
      validate={validate}
      form={form}
    />
  );
};

AnnouncementsModalContainer.propTypes = {
  onGetBuildings: PropTypes.func.isRequired,
  onUpdateAnnouncement: PropTypes.func.isRequired,
  onCreateAnnouncement: PropTypes.func.isRequired,
  onSendAnnouncement: PropTypes.func.isRequired,
  onFieldsTouch: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  getAnnouncements: PropTypes.func.isRequired,
  onDeleteAnnouncement: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onShowConfirmModal: PropTypes.func.isRequired,
  formValues: PropTypes.instanceOf(Object).isRequired,
  initialValues: PropTypes.instanceOf(Object),
  listParams: PropTypes.instanceOf(Object),
  buildings: PropTypes.instanceOf(Array),
};

const filterBuildingsByParent = (buildings, pmcsIds) => {
  if (!buildings?.length || !pmcsIds?.length) return [];
  return buildings.filter(({ parent }) => pmcsIds.includes(parent?.id));
};

const getAvailableAnnouncementsRoles = (isOn) => {
  const rolesNames = [
    'pmc_owner',
    'property_manager',
    'superintendent',
    'handyman',
    'owner',
    'board_member',
    'tenant',
    'concierge',
  ];
  const roles = {};
  rolesNames.forEach((roleName) => {
    roles[roleName] = isOn;
  });
  return roles;
};

const getInitialValues = (values) => {
  if (!Object.values(values).length) return {};
  let {
    pmcs,
    buildings,
    accessControlSystem,
    cameraSystem,
    startLaunchTime,
    endLaunchTime,
    roles: rolesIds,
    sent,
    isCopy,
    ...rest
  } = values;
  const roles = getAvailableAnnouncementsRoles(false);
  rolesIds.forEach((roleId) => {
    const roleName = getRoleName(roleId);
    roles[roleName] = true;
  });
  if (isCopy) {
    rest.id = null;
    sent = false;
  }
  return {
    ...rest,
    pmcs: pmcs?.map(({ id }) => id),
    pmcsOptions: pmcs,
    buildings: buildings?.map(({ id }) => id),
    buildingsOptions: buildings,
    access_control_system: accessControlSystem,
    camera_system: cameraSystem,
    launch_date_after: startLaunchTime,
    launch_date_before: endLaunchTime,
    roles,
    sent,
  };
};

const mapStateToProps = (state, ownProps) => {
  const pmcsList = pmcSelectors.getPmcsList(state);
  const buildings = buildingSelectors.getBuildings(state);
  const formValues = getFormValues(form)(state) || {};
  const isFormSubmitting = isSubmitting(form)(state);

  const { announcementsData } = ownProps;

  const defaultValues = {
    allProperties: true,
    pmcs: [],
    buildings: [],
    access_control_system: null,
    camera_system: null,
    launch_date_after: null,
    launch_date_before: null,
    subject: '',
    message: '',
    roles: getAvailableAnnouncementsRoles(true),
  };
  const initialValues = announcementsData?.id
    ? getInitialValues(announcementsData)
    : defaultValues;

  const { pmcs } = formValues;
  const buildingsList = pmcs?.length
    ? filterBuildingsByParent(Object.values(buildings), pmcs)
    : Object.values(buildings);

  return {
    pmcs: pmcsList,
    initialValues,
    buildings: buildingsList,
    formValues,
    isFormSubmitting,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onLoad: () => getAllPmcReducedData(dispatch),
  onGetBuildings: (queryParams) =>
    getBuildingsForFilter(dispatch, {
      ...queryParams,
      withReset: true,
      frozen: false,
    }),
  onCreateAnnouncement: (data) => createAnnouncement(dispatch, data),
  onUpdateAnnouncement: (id, data) => updateAnnouncement(dispatch, id, data),
  onDeleteAnnouncement: (id) => deleteAnnouncement(dispatch, id),
  onSendAnnouncement: (id) => sendAnnouncement(dispatch, id),
  onFieldsTouch: (fields) => dispatch(touch(form, ...fields)),
  onFieldChange: (field, value) => dispatch(change(form, field, value)),
  onModalClose: () => onModalClose(dispatch),
  onShowConfirmModal: (modalProps) => showConfirmModal(dispatch, modalProps),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AnnouncementsModalContainer);
